import amaLog, { amaAlert, amaWarn } from '../helpers/log';
import loadScript from '../resource/loadScript';
import { pbjs } from '../prebid';
import { getCookie, setCookie } from '../storage/cookies';
import { adsFullscreen } from './madington';

/**
 * Fetches the user's tags from the recommendation API and stores them in a cookie.
 * The tags are sent on as key-values — the audience segments themselves are built
 * in GAM from those key-values.
 * This function is fire-and-forget - it will not block ad initialization on failure.
 * The cookie expires after 6 hours and is skipped if already present.
 */
export const fetchAndStoreUserTags = async (): Promise<void> => {
  const cookieName = 'allerM_seg2';

  // Skip if cookie already exists
  if (getCookie(cookieName)) {
    amaLog('[NO] user tags cookie already exists, skipping fetch');
    return;
  }

  try {
    amaLog('[NO] Fetching user tags from', window.location.origin);
    const response = await fetch('/app/recommendation-data/api/v1/users/me/tags');

    if (!response.ok) {
      amaWarn(`[NO] user tags request failed with status ${response.status}`);
      return;
    }

    const data = await response.json();

    // Validate response has tags array before storing
    if (!data?.tags || !Array.isArray(data.tags)) {
      amaWarn('[NO] user tags response missing tags array');
      return;
    }

    const encodedData = btoa(encodeURIComponent(JSON.stringify(data)));

    setCookie(cookieName, encodedData, 0.25); // Expires in 6 hours (0.25 days)

    amaLog(`[NO] user tags cookie set with ${data.tags.length} tags`);
  } catch (error) {
    amaWarn('[NO] Failed to fetch user tags:', error);
  }
};

loadScript('https://cdn.jsdelivr.net/npm/a11y-dialog@8/dist/a11y-dialog.min.js')
  .then(() => {
    amaLog('a11y-dialog loaded');
  })
  .catch((e) => {
    console.error('a11y-dialog failed to load', e);
  });

let skinElem = null as any; // TODO: Should not be "any" maybe
const calculateOffset = () => {
  document.querySelectorAll('[data-load-on]').forEach((element: any) => {
    let elementOffset = getTopOffset(element);
    let ElementHeight = getOffsetHeight(element);

    element.setAttribute('data-top-offset', String(elementOffset));
    element.setAttribute('data-offsetheight', String(ElementHeight));
  });
};

calculateOffset();

// MOVE the custom init from admanager
const calculateStopPoint = () => {
  // Create a list of sticky adunits
  const stickies = Array.prototype.slice.call(document.querySelectorAll('.adunit[class*=sticky-right]'));
  if (stickies.length) {
    let scrollTop = window.pageYOffset;
    let fullscreens = Array.prototype.slice
      .call(document.querySelectorAll('.adunit[class*=fullscreen]'))
      .map(function (element) {
        let rect = element.getBoundingClientRect();
        return {
          element: element,
          offsetTop: Math.round(rect.top + scrollTop),
        };
      });

    let main = document.querySelector('main');

    if (fullscreens.length && main) {
      let mainRect = main.getBoundingClientRect();
      let mainOffsetTop = Math.round(mainRect.top + scrollTop);
      let mainOffsetHeight = Math.round(mainRect.height);

      stickies
        .map(function (element) {
          let rect = element.getBoundingClientRect();
          let parentRect = element.parentElement.getBoundingClientRect();
          return {
            element: element,
            offsetTop: Math.round(parentRect.top + scrollTop),
            offsetHeight: Math.round(rect.height),
          };
        })
        .forEach(function (sticky, i, all) {
          // Loop through all stickies to find their stopPoints
          let stickyOffsetTop = sticky.offsetTop;
          // Because we don't save the space for the sticky-adunit, we don't know the height of
          // it before we get a reply from DFP when it's lazyloaded. If we don't know the height of it,
          // we just assume it's 1000.
          let stickyOffsetHeight = sticky.offsetHeight || 1000;
          let parent = sticky.element.parentElement;
          let nextSticky = all[i + 1];

          if (fullscreens.length) {
            for (let j = 0; j < fullscreens.length; j++) {
              let fullscreen = fullscreens[j];
              let fullscreenOffsetTop = fullscreen.offsetTop;

              if (nextSticky && nextSticky.offsetTop < fullscreenOffsetTop) {
                // If the next sticky is higher up than the next fullscreen set the stop point
                let stopPoint = nextSticky.offsetTop - stickyOffsetHeight - 100; // 100px works as a spacer
                sticky.element.setAttribute('data-stop-point', stopPoint);
                break;
              } else {
                // Set stop point for the next fullscreen
                let stopPoint = fullscreenOffsetTop - stickyOffsetHeight - 100; // 100px works as a spacer
                sticky.element.setAttribute('data-stop-point', stopPoint);
                parent.style.height = fullscreenOffsetTop - stickyOffsetTop - 30 + 'px'; // 30px compensates for Annonse bar
                fullscreens.splice(j, 1);
                break;
              }
            }
          } else {
            if (nextSticky) {
              // If there's the next sticky set the stop point
              let stopPoint = nextSticky.offsetTop - stickyOffsetHeight - 100; // 100px works as a spacer
              sticky.element.setAttribute('data-stop-point', stopPoint);
            } else {
              // No more stop points under this sticky. Set sticky height to the remaining length of <main>
              let stopPoint = Math.round(mainOffsetTop + mainOffsetHeight);
              sticky.element.setAttribute('data-stop-point', stopPoint);
              parent.style.height = mainOffsetTop + mainOffsetHeight - stickyOffsetTop + 'px';
            }
          }
        });
    }
    // Recalculate offset top and bottom threshold of stickies in ads-defer.js after their parents got height
    adsFullscreen.reflow();
  }
};

calculateStopPoint();

const setWallpaper = (message: any) => {
  const $frame = document.querySelector(
    'iframe[name="' + message.frame + '"], iframe[id="' + message.frame + '"]',
  ) as HTMLElement;

  const $wallpaper = skinElem;

  if ($frame === null) {
    console.log('am.libs.ads.setWallpaper > Adunit iframe missing name attribute.');
    return;
  }

  const $topIframe = document.createElement('iframe');
  const $leftIframe = document.createElement('iframe');
  const $rightIframe = document.createElement('iframe');
  $topIframe.id = 'horseshoe-ad-background-top';
  $leftIframe.id = 'horseshoe-ad-background-left';
  $rightIframe.id = 'horseshoe-ad-background-right';
  $topIframe.name = 'horseshoe-ad-background-top';
  $leftIframe.name = 'horseshoe-ad-background-left';
  $rightIframe.name = 'horseshoe-ad-background-right';
  $topIframe.ariaLabel = 'Advertisement';
  $leftIframe.ariaLabel = 'Advertisement';
  $rightIframe.ariaLabel = 'Advertisement';

  const topIframeImg = `<img src='${message.src}' width='100%' height='100%' alt='Top ad background' style="object-fit: none; object-position: center top;" />`;
  const leftIframeImg = `<img src='${message.src}' width='100%' height='100%' alt='Left ad background' style="object-fit: none; object-position: left top;" />`;
  const rigthIframeImg = `<img src='${message.src}' width='100%' height='100%' alt='Right ad background' style="object-fit: none; object-position: right top;" />`;

  if ($wallpaper === null) {
    console.log('am.libs.ads.setWallpaper > ad-skin element missing. Going legacy');
    // here is a good place for fallback, please remove me when migration is done
    let $main = document.querySelector('main') as HTMLElement;
    if ($main === null) {
      $main = document.querySelector('article.article-entity');
    }

    // Insert wallpaper background as an iframe in top ad
    $frame.parentNode.insertBefore($topIframe, $frame);
    handleIframeContent($topIframe, topIframeImg);

    $main.insertAdjacentHTML(
      'beforebegin',
      '<a id="adunit-wallpaper-left" target="_blank" href="#"></a><a id="adunit-wallpaper-right" target="_blank" href="#"></a>',
    );

    const top = getOffsetHeight($frame);

    const $wallpaperLeft = document.querySelector('#adunit-wallpaper-left') as HTMLElement;
    // Insert wallpaper background as an iframe in left ad
    $wallpaperLeft.appendChild($leftIframe);
    handleIframeContent($leftIframe, leftIframeImg);
    $wallpaperLeft.style.top = top + 'px';
    $wallpaperLeft.setAttribute('href', message.url);

    const $wallpaperRight = document.querySelector('#adunit-wallpaper-right') as HTMLElement;
    // Insert wallpaper background as an iframe in right ad
    $wallpaperRight.appendChild($rightIframe);
    handleIframeContent($rightIframe, rigthIframeImg);
    $wallpaperRight.style.top = top + 'px';
    $wallpaperRight.setAttribute('href', message.url);
    return $frame;
  }

  // Insert wallpaper background as an iframe in top ad
  $frame.parentNode.insertBefore($topIframe, $frame);
  handleIframeContent($topIframe, topIframeImg);

  $wallpaper.firstElementChild.insertAdjacentHTML(
    'afterbegin',
    '<a id="adunit-wallpaper-left" class="ad-skin__wallpaper ad-skin__wallpaper--left" target="_blank" href="#"></a><a id="adunit-wallpaper-right" class="ad-skin__wallpaper ad-skin__wallpaper--right" target="_blank" href="#"></a>',
  );

  const $wallpaperLeft = document.querySelector('.ad-skin__wallpaper--left') as HTMLElement;
  // Insert wallpaper background as an iframe in left ad
  $wallpaperLeft.appendChild($leftIframe);
  handleIframeContent($leftIframe, leftIframeImg);
  $wallpaperLeft.style.top = '26px';
  $wallpaperLeft.setAttribute('href', message.url);

  const $wallpaperRight = document.querySelector('.ad-skin__wallpaper--right') as HTMLElement;
  // Insert wallpaper background as an iframe in right ad
  $wallpaperRight.appendChild($rightIframe);
  handleIframeContent($rightIframe, rigthIframeImg);
  $wallpaperRight.style.top = '26px';
  $wallpaperRight.setAttribute('href', message.url);

  calculateScrollHeight($wallpaper);

  return $frame;
};
function getOffsetHeight(element: any) {
  return element.offsetHeight;
}

const showPreloadedSlot = function showPreloadedSlot(id: string) {
  const element = document.getElementById(id);

  if (!element) {
    console.log('am.libs.ads.showPreloadedSlot > Element not found.');
    return;
  }
  const originalId = id.replace('-pre', '');

  element.id = originalId;
  googletag.cmd.push(function () {
    googletag.display(originalId);
  });
  return element;
};

function shrinkFullscreenPlacement(adunit: any, width: any, height: any) {
  adunit.classList.add('static');
  adunit.style.setProperty('width', width + 'px', 'important');
  adunit.style.height = height + 'px';
  adunit.setAttribute('data-offsetheight', height);

  adunit.classList.remove('large-fullscreen-x');
  adunit.classList.remove('medium-fullscreen-x');
  adunit.classList.remove('small-fullscreen-x');
  if (width === 980) {
    adunit.classList.add('large-980xauto');
  }

  // Fix for where shrink does not center the creative in app
  const googleDiv = adunit.firstElementChild as HTMLElement;
  if (googleDiv) {
    googleDiv.style.display = 'flex';
    googleDiv.style.justifyContent = 'center';
  }

  // Set margins auto with important to override any inline styles set by DFP.
  // NOTE: This is only happening on Sol for some reason where ad is shifted to the left in browser and out to the right on mobile app
  adunit.style.setProperty('margin-left', 'auto', 'important');
  adunit.style.setProperty('margin-right', 'auto', 'important');
  adunit.style.setProperty('max-width', '100%', 'important');

  // Recalculate offset of ads when fullscreen placements are shrunk
  calculateOffset();
}

// This is needed to have full browser compatibility
function handleIframeContent(iframe: HTMLIFrameElement, iframeImg: string) {
  if (iframe.contentDocument.readyState === 'complete') {
    iframe.contentDocument.body.innerHTML = iframeImg;
    iframe.contentDocument.body.style.border = '0';
    iframe.contentDocument.body.style.margin = '0';
  } else {
    iframe.addEventListener('load', () => {
      iframe.contentDocument.body.innerHTML = iframeImg;
      iframe.contentDocument.body.style.border = '0';
      iframe.contentDocument.body.style.margin = '0';
    });
  }
}

const setSize = (message: any) => {
  // 1. Resolve the adunit element
  let $adunit: HTMLElement | null;

  if (message.id) {
    $adunit = document.getElementById(message.id);
  } else {
    const iframe = document.querySelector(`iframe[name="${message.frame}"]`);
    $adunit = iframe?.parentElement?.parentElement ?? null;
  }

  if (!$adunit) {
    console.warn('am.libs.ads.setSize > Adunit iframe missing name attribute.');
    return;
  }

  // 2. Validate the incoming size
  const sizeMatch = message.size.match(/small|medium|large/i);

  if (!sizeMatch) return;

  const sizePrefix = sizeMatch[0].toLowerCase();

  // 3. Remove any existing `<size>-xyz` class
  [...$adunit.classList].forEach((cls) => {
    if (cls.startsWith(sizePrefix + '-')) {
      $adunit.classList.remove(cls);
    }
  });

  // 4. Add the new size class (e.g., "medium")
  $adunit.classList.add(message.size);
};

/**
 * Calculates the offset height and offset to top off document
 * is normally at the top of the next fullscreen ad.
 * @private
 * @memberof am.libs.ads
 **/

export function getTopOffset(element: HTMLElement) {
  let topOffset = 0;

  while (element) {
    topOffset += element.offsetTop + element.clientTop;
    element = element.offsetParent as HTMLElement | null;
  }
  return topOffset;
}
// Calculates the offset height and offset to top off document is normally at the top of the next fullscreen ad.
const calculateScrollHeight = function (el: any) {
  if (el) {
    const topOffset = getTopOffset(el as HTMLElement);
    el.setAttribute('data-top-offset', topOffset);
    el.setAttribute('data-offsetheight', el.offsetHeight);

    const paywall = document.querySelector('.pw-top') as HTMLElement;
    const firstFullscreen = document.querySelector('[id^="ad-fullscreen"]') as HTMLElement;
    const fullWidthImage = document.querySelector(
      'div.body-copy[itemprop="articleBody"] figure.fullwidth, .content.fullwidthTarget',
    ) as HTMLElement;
    const parallax = document.querySelector('.parallax-container [data-parallax-fullwidth]') as HTMLElement;
    const main = document.querySelector('main') as HTMLElement;

    let fsTopOffset = 3000; // Default scrolling height (px)
    const safeDistence = 30; // Distence between Skin and Fullscreen (px)

    // Collect all potential stopping elements and their normalized top offsets
    const stoppingElements = [];
    if (paywall) {
      stoppingElements.push(paywall.offsetTop - 100);
    }

    if (firstFullscreen) {
      const fullscreenOffset = window.scrollY + firstFullscreen.getBoundingClientRect().top;
      stoppingElements.push(fullscreenOffset);
    }

    if (fullWidthImage) {
      const fullWidthOffset = window.scrollY + fullWidthImage.getBoundingClientRect().top;
      stoppingElements.push(fullWidthOffset);
    }

    if (parallax) {
      const parallaxOffset = window.scrollY + parallax.getBoundingClientRect().top;
      stoppingElements.push(parallaxOffset);
    }

    if (main) {
      const mainOffset = main.offsetTop + main.offsetHeight;
      stoppingElements.push(mainOffset);
    }

    // Find the minimum offset (highest element on page)
    if (stoppingElements.length > 0) {
      fsTopOffset = Math.floor(Math.min.apply(null, stoppingElements));
    }
    const skinOffsetheight = fsTopOffset - topOffset - safeDistence;
    if (skinOffsetheight > 0) {
      el.style.height = skinOffsetheight + 'px';
      el.setAttribute('data-offsetheight', skinOffsetheight);
    }
  }
};

let topscrollFormatMobileHasRun = false;

function topscrollFormatMobile() {
  if (topscrollFormatMobileHasRun === true) {
    return;
  }
  topscrollFormatMobileHasRun = true;

  // Get apos and row element.
  const apos = document.getElementById('apos-top-mobile1');
  const row = apos?.parentElement;

  // Set topscroll-main css class
  const main = document.getElementById('main');
  main?.classList?.add('topscroll-main');
  // Move row element before main element on front page.
  if (window.Aller?.hydrated?.pageType === 'front') {
    main?.insertAdjacentElement('beforebegin', row);
    main.style.paddingTop = '1.25rem';
  }

  // Update classes
  row?.classList?.add('topscroll-mobile');
  // For some reason the google div element is inline-block and I dont know where its coming from
  let googleTag = apos?.childNodes[0].firstChild as HTMLElement;
  if (googleTag) {
    googleTag.style.display = 'block';
  }

  // Remove header margin for KK
  const header_kk = document.getElementById('kk-header');
  if (header_kk) {
    header_kk.style.marginBottom = 'unset';
  }

  // Remove the bottom-margin of the subheader above
  const subheader = document.getElementsByClassName('subheader')?.[0] as HTMLElement;
  if (subheader?.style?.marginBottom) {
    subheader.style.marginBottom = '0';
  }

  const dinSideToolboxParent = document.querySelector('.toolbox')?.parentElement;
  dinSideToolboxParent?.classList.add('topscroll-toolbox-row');

  // Show topscroll.
  apos.classList.remove('hidden');
}

let topscrollFormatDesktopHasRun = false;

function topscrollFormatDesktop() {
  if (topscrollFormatDesktopHasRun === true) {
    return;
  }
  topscrollFormatDesktopHasRun = true;
  const pageType = window.Aller?.hydrated?.pageType;

  // Target the ad position that topscroll is delivered in
  const topbanner = document.getElementById('ad-topbanner1-1');

  // Get the row element that the ad is positioned in
  const row = topbanner?.parentElement?.parentElement;

  // Set topscroll-main css class
  const main = document.getElementById('main');
  main?.classList.add('topscroll-main');
  if (pageType === 'front') {
    main?.insertAdjacentElement('beforebegin', row);
    main.style.paddingTop = '1.25rem';
  }

  // Update classes
  row?.classList?.add('topscroll');

  // For some reason the google div element is inline-block and I dont know where its coming from
  let googleTag = topbanner?.firstChild as HTMLElement;
  if (googleTag) {
    googleTag.style.display = 'block';
  }

  // Remove stickes when topscroll delivers
  const stickies = document.getElementsByClassName('ad-skin')[0];
  if (stickies) {
    stickies.remove();
  }

  // Remove specified height and width for child div
  const childElement = topbanner.children.item(0) as HTMLElement;
  childElement?.style.setProperty('height', '100%');
  childElement?.style.setProperty('width', '100%');
  topbanner?.classList.remove('large-980x300');

  // Remove header margin for KK
  const header_kk = document.getElementById('kk-header');
  if (header_kk) {
    header_kk.style.marginBottom = 'unset';
  }

  // Remove the bottom-margin of the subheader above
  const subheader = document.getElementsByClassName('subheader')?.[0] as HTMLElement;
  if (subheader?.style?.marginBottom) {
    subheader.style.marginBottom = '0';
  }

  const dinSideToolboxParent = document.querySelector('.toolbox')?.parentElement;
  dinSideToolboxParent?.classList.add('topscroll-toolbox-row');
}

export function custom_no() {
  // Get skin element
  skinElem = document.querySelector('.ad-skin');

  /**
   * adinfoDialog is a modal which will popup when a user clicks on the wheel on upper left corner of ads
   */
  const adinfoDialog = document.getElementById('adinfo-dialog');

  /**
   * Wee need main element for the modal popup
   */
  const pageMainElement = document.getElementsByTagName('main')[0];

  // https://github.com/edenspiekermann/a11y-dialog
  // For popup modal when you click on the wheel on ads
  let dialog = null as any;

  googletag.pubads().addEventListener('slotRenderEnded', function (event) {
    const id = event.slot.getSlotElementId();
    const adunit = document.querySelector('.adunit#' + id);
    const requestedOn = adunit ? adunit.getAttribute('data-load-on') : 0;
    const isEmpty = event.isEmpty;

    /* this block: Settings icon in the corner of ads  */
    if (!isEmpty) {
      // Make sure we do not add cogs to wolverine ads
      if (!adunit.classList.contains('wolverine-ads')) {
        let adInfoDiv = document.createElement('button');
        adInfoDiv.className = 'settings-alt-2';
        adInfoDiv.type = 'button';
        adInfoDiv.setAttribute('data-a11y-dialog-show', 'adinfo-dialog');
        adInfoDiv.setAttribute('aria-label', 'Annonseinnstillinger');
        adInfoDiv.setAttribute(
          'style',
          'position:absolute; left:0px; top:0px; width: 30px !important; background-color: white; opacity: 0.5; filter: alpha(opacity=50); cursor: pointer; color:#333; margin:0; padding:0; line-height:26px; font-size:22px; border-width:0px; border-radius:0px; z-index:inherit;',
        );
        adInfoDiv.textContent = '⚙';
        adunit.appendChild(adInfoDiv);
        adInfoDiv.addEventListener('click', function () {
          if (!dialog && window.A11yDialog && adinfoDialog && pageMainElement) {
            dialog = new window.A11yDialog(adinfoDialog, pageMainElement);
          }
          if (dialog) {
            dialog.show();
          }
        });
      }
    }

    try {
      if (!event.isEmpty && event.hasOwnProperty('size') && event.size[0] === 2 && event.size[1] === 2) {
        if (event.slot.getSlotElementId() === 'ad-topbanner1-1') {
          topscrollFormatDesktop();
        } else if (event.slot.getSlotElementId() === 'ad-top-mobile1-1') {
          topscrollFormatMobile();
        }
      }
    } catch (error) {
      console.error('GAM topscroll failed: ', error);
    }

    if (
      !event.isEmpty &&
      event.slot.getSlotElementId() === 'ad-sticky-bottom1-1' &&
      event.hasOwnProperty('size') &&
      event.size[0] === 390 &&
      event.size[1] === 60
    ) {
      // Create an observer instance to follow when an element is showing in the DOM
      const onVisible = (element: any, callback: any) => {
        new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              callback(element);
              observer.disconnect();
            }
          });
        }).observe(element);
      };

      // Remove popup slider from showing on top of ad
      const slideInBanner = document.getElementById('slide-in-banner') as HTMLElement;
      onVisible(slideInBanner, () => {
        slideInBanner.remove();
      });

      // Remove popup for download app from showing on top of ad
      const appBanner = document.getElementById('app-banner') as HTMLElement;
      onVisible(appBanner, () => {
        appBanner.remove();
      });

      // Disable overlay that shows with app banner above from showing and making the page unresponsive
      const appBannerOverlay = document.getElementsByClassName('open-in-app-overlay')[0];
      onVisible(appBannerOverlay, () => {
        appBannerOverlay.remove();
      });

      // Remove logic that makes the page unscrollable
      const disabledScreen = document.getElementsByClassName('disable-page-scroll disable-page-scroll_ios')[0];
      disabledScreen?.classList.remove('disable-page-scroll', 'disable-page-scroll_ios');
    }

    if (
      !event.isEmpty &&
      event.slot.getSlotElementId() === 'ad-topbanner1-1' &&
      event.hasOwnProperty('size') &&
      event.size
    ) {
      // There is an inconsistency in wolverine where topbanner is not center due to justify-content: flex-start.
      // We add a class to topbanners closest .row, that is topbanners grand parent
      const topbanner = document.getElementById(event.slot.getSlotElementId());
      const containerRow = topbanner.parentElement.parentElement;
      if (containerRow.classList.contains('row')) {
        containerRow.classList.add('ad-skin-row');
      }

      // Topbanner with companions wallpaper. Show sticky-right-top
      if (event.size[0] === 1010) {
        console.log('Topbanner with horseshoe and companions wallpaper. Show sticky-right-top1');
        //Deactivate DAI
        // if (am.libs.dai_sticky_right_top) am.libs.dai_sticky_right_top.disable();
        showPreloadedSlot('ad-sticky-right-top1-1');
        showPreloadedSlot('ad-sticky-left-top1-1');
        skinElem.classList.add('ad-skin--horseshoe');
      }

      // Topbanner with wallpaper. Hide sticky right and deactivate DIA
      if (event.size[0] === 1000) {
        console.log('Topbanner with wallpaper. Hide sticky right and deactivate DAI');
        //Deactivate DAI
        // if (am.libs.dai_sticky_right_top) am.libs.dai_sticky_right_top.disable(); // TODO: Check if this is still needed
        skinElem.classList.add('ad-skin--wallpaper');
      }

      // Regular Topbanner, show sticky-right-top and calculate its scrolling height
      if ((event.size[0] as number) < 1000) {
        console.log('Regular Topbanner. Show sticky-right-top1');
        const rightTop = showPreloadedSlot('ad-sticky-right-top1-1');
        const leftTop = showPreloadedSlot('ad-sticky-left-top1-1');
        if (rightTop && rightTop.parentElement) {
          calculateScrollHeight(rightTop.parentElement);
        }
        if (leftTop && leftTop.parentElement) {
          calculateScrollHeight(leftTop.parentElement);
        }
        skinElem.classList.add('ad-skin--noskin');
        // try {
        //   am.libs.dai_sticky_right_top = new DAI(
        //     'ad-sticky-right-top1',
        //     'ad-dai-sticky-right-top',
        //     'dai-sticky-right-top',
        //   );
        // } catch (e) {
        //   //Silent error...
        // }
      }
    }
    // Handle shrinking of fullscreen positions when smaller than 1920x1080 ads are delivered
    if (
      !isEmpty &&
      adunit.className.indexOf('fullscreen-x') > 1 &&
      event.hasOwnProperty('size') &&
      event.size &&
      (event.size[0] as number) > 1 &&
      event.size[0] !== 1920 &&
      event.size[1] !== 1080
    ) {
      shrinkFullscreenPlacement(adunit, event.size[0], event.size[1]);
    }
  });

  googletag.pubads().addEventListener('slotOnload', function (event) {
    const id = event.slot.getSlotElementId();
    const adunit = document.getElementById(id);
    const iframe = adunit.querySelector('iframe');

    // If sticky skyscraper, narrow the creative for better viewability // TODO: This needs testing
    if (id.indexOf('sticky-right') !== -1 || id.indexOf('sticky-left') !== -1) {
      let iframeWidth = 160;
      if (iframe && iframe.width && parseInt(iframe.width) > 0) {
        // there is a width on the iframe and bigger than 0, there by not a fluid.
        if (parseInt(iframe.width) > 1) {
          iframeWidth = parseInt(iframe.width);
        } else if (iframe.getBoundingClientRect() && iframe.getBoundingClientRect().width > 1) {
          // size of prebid iframe is 1x1px. Need to use getBoundingClientRect to get the correct width
          iframeWidth = iframe.getBoundingClientRect().width;
        }

        adunit.style.width = iframeWidth + 'px';
        adunit.style.minWidth = iframeWidth + 'px';

        if (parseInt(iframe.width) < 300) adunit.style.marginLeft = '0';
      }
    }
  });

  // Listen for incomming messages from ads.
  window.addEventListener('message', function (event) {
    // Render wallpaper ad format
    if (event.data.callback == 'db.libs.ads.setWallpaper') {
      setWallpaper(event.data);
    }

    if (event.data.callback == 'db.libs.ads.setSize') {
      setSize(event.data);
    }

    // Expand wallpaper
    if (event.data.callback == 'db.libs.ads.expandWallpaper') {
      const topbanner = document.querySelector('#ad-topbanner1-1') as HTMLElement;
      topbanner.style.height = event.data.height || '700px';

      // Get iframes
      const firstIframe = topbanner.firstElementChild.firstElementChild;
      const lastIframe = topbanner.firstElementChild.lastElementChild;

      // Remove height attribute of second iframe
      lastIframe.removeAttribute('height');

      // Create anchor tag and assign click url then add styling
      const anchor = document.createElement('a');
      anchor.href = event.data.url;
      anchor.classList.add('anchor-expandable-wallpaper');

      // Insert anchor tag in div above iframe
      firstIframe.parentNode.insertBefore(anchor, firstIframe);
    }

    // Make wallpaper support taller background image.
    if (event.data.callback == 'db.libs.ads.wallpaperMoreHeight') {
      const left = document.getElementById('adunit-wallpaper-left');
      const right = document.getElementById('adunit-wallpaper-right');
      left.style.height = '1300px';
      right.style.height = '1300px';
    }

    // Add offset for height of wallpaper so it meets travels down to fullscreen ad
    if (event.data.callback == 'db.libs.ads.moreWallpaperOffsetHeight') {
      const skin = document.querySelector('.ad-skin') as HTMLElement;
      const curHeight = skin.getAttribute('data-offsetheight');
      skin.style.height = +curHeight + 400 + 'px';
    }

    // Remove fullscreen ad-label. In use but should be removed since we have dynamic version below this
    if (event.data.callback == 'db.libs.ads.removeAdSlugFullscreen1') {
      const adElement = document.querySelector('#ad-fullscreen1-1') as HTMLElement;
      if (adElement === null) {
        console.log('No ad element');
      } else {
        adElement.classList.add('adunit-content');
        const adElementCogWheel = adElement.querySelector('.settings-alt-2') as HTMLElement;
        if (adElementCogWheel) {
          adElementCogWheel.style.display = 'none';
        }
      }
    }

    if (event.data.callback == 'db.libs.ads.removeAdSlug') {
      const adElement = document.querySelector('#ad-' + event.data.pos) as HTMLElement;
      if (adElement === null) {
        console.log('am.libs.ads.removeAdSlug > Can not find element ad-' + event.data.pos);
      } else {
        adElement.classList.add('adunit-content');
        const adElementCogWheel = adElement.querySelector('.settings-alt-2') as HTMLElement;
        if (adElementCogWheel) {
          adElementCogWheel.style.display = 'none';
        }
      }
    }

    // Collapse wallpaper - When topbanner is expanded to show extended wallpaper
    // this can be used to collapse it again
    if (event.data.callback == 'db.libs.ads.collapseWallpaper') {
      const topbanner = document.querySelector('#ad-topbanner1') as HTMLElement;
      topbanner.style.height = '300px';
    }

    // When wallpaper has companions stickies append these images to sticky positions
    if (event.data.callback == 'db.libs.ads.wallpaperCompanions') {
      if (event.data.stickyLeft && event.data.stickyRight) {
        console.log('Topbanner with static images. Show left and right stickies');

        // Left width of sticky
        const stickyLeftWidth = event?.data?.stickyLeftWidth;
        // Right width of sticky
        const stickyRightWidth = event?.data?.stickyRightWidth;

        const leftPos = document.getElementById('ad-sticky-left-top1-pre');
        const rightPos = document.getElementById('ad-sticky-right-top1-pre');

        // Create anchor tags to wrap images and assign link
        const aLeft = document.createElement('a');
        const aRight = document.createElement('a');

        leftPos.setAttribute('style', `width:${stickyLeftWidth}px; min-width:${stickyLeftWidth}px; margin-left:0px`);
        rightPos.setAttribute('style', `width:${stickyRightWidth}px; min-width:${stickyRightWidth}px; margin-left:0px`);
        aLeft.setAttribute('style', 'width:inherit; height:inherit; display:block;');
        aRight.setAttribute('style', 'width:inherit; height:inherit; display:block;');
        aLeft.href = event?.data?.url;
        aRight.href = event?.data?.url;

        // Add anchors around divs
        leftPos.appendChild(aLeft);
        rightPos.appendChild(aRight);

        // Create iframe
        const leftIframe = document.createElement('iframe');
        const rightIframe = document.createElement('iframe');
        leftIframe.setAttribute('style', 'height: inherit; width: inherit;');
        rightIframe.setAttribute('style', 'height: inherit; width: inherit;');

        // Add iframe to anchor tags
        aLeft.appendChild(leftIframe);
        aRight.appendChild(rightIframe);

        // Add anchor tags to div posistions
        leftPos.appendChild(aLeft);
        rightPos.appendChild(aRight);

        // Create images, assign src and append to anchor tag
        const stickyImgRight = `<img src='${event?.data?.stickyRight}' width='inherit' height='inherit' alt='Right sticky ad image'" />`;
        const stickyImgLeft = `<img src='${event?.data?.stickyLeft}' width='inherit' height='inherit' alt='Left sticky ad image'" />`;

        // Add images to iframes
        handleIframeContent(leftIframe, stickyImgLeft);
        handleIframeContent(rightIframe, stickyImgRight);

        // Change skin element class to show stickies
        skinElem?.classList.replace('ad-skin--wallpaper', 'ad-skin--horseshoe');
      }
    }
  });

  window.pbjs.onEvent('bidWon', function (data: any) {
    if (data.adUnitCode.indexOf('ad-fullscreen') > -1 && data.height >= 120 && data.height <= 600) {
      let adunit = document.querySelector('.adunit#' + data.adUnitCode);
      shrinkFullscreenPlacement(adunit, data.width, data.height);
    }
    if (data.adUnitCode == 'ad-topbanner1' && data.size == '2x2') {
      topscrollFormatDesktop();
    } else if (data.adUnitCode == 'ad-top-mobile1' && data.size == '2x2') {
      topscrollFormatMobile();
    }
    // If its fullscreen placement and 1920x1080, 1x1, 1x2 or 1x4
    // Transform it to a fullscreen by setting its iframe to 100%
    if (
      data.adUnitCode.indexOf('ad-fullscreen') > -1 &&
      ((data.width === 1920 && data.height === 1080) ||
        (data.width === 1 && data.height <= 2) ||
        (data.width === 1 && data.height === 4))
    ) {
      let adunit = document.querySelector('.adunit#' + data.adUnitCode + '>div>iframe') as HTMLElement;
      adunit.style.width = adunit.style.height = '100%';
    }
  });

  // Check if we have a sticky bottom ad.
  const adStickyBottom = document.getElementById('ad-sticky-bottom1-1');

  if (adStickyBottom) {
    // Add close button to ad
    const afterElement = document.createElement('span');
    afterElement.classList.add('ad-sticky-bottom1-close');
    afterElement.textContent = '×';
    afterElement.addEventListener('click', function () {
      adStickyBottom.style.display = 'none';
      afterElement.style.display = 'none';
    });
    adStickyBottom.insertAdjacentElement('afterend', afterElement);

    // Add a listener for sticky ads
    window.addEventListener('message', (event) => {
      // Display sticky ad when we receive message from parent
      if (event.data === 'ad-sticky-bottom1-loaded') {
        adStickyBottom.classList.add('loaded');
        afterElement.classList.add('loaded');
      }
      // Toggle fullscreen when we receive message from parent
      else if (event.data === 'ad-sticky-bottom1-toggle-fullscreen') {
        adStickyBottom.classList.toggle('fullscreen');
        afterElement.classList.toggle('fullscreen');
      }
    });
  }
}

export const initNoStickies = () => {
  try {
    const tb = document.querySelector('#ad-topbanner1') as HTMLElement;

    const mainFront = document.querySelector('main.front');
    const topbannerParent = tb.parentElement as HTMLElement | null;

    const fromXavier = topbannerParent?.dataset?.from === 'xavier';
    if ((mainFront || fromXavier) && tb) {
      const stickyunderfullscreen = document.querySelectorAll('.sticky-right');
      stickyunderfullscreen.forEach(function (item) {
        if (item.parentElement.classList.contains('adunit-wrapper')) {
          item.parentElement.classList.add('adunit-wrapper--sticky');
          item.parentElement.classList.remove('sticky-ad');
        }
      });
      const sibling = tb.parentElement.nextElementSibling;
      if (sibling && sibling.classList.contains('adunit-wrapper')) {
        sibling.parentNode.removeChild(sibling);
        const adunitPath = tb.dataset.slot;
        // Reference: https://github.com/dbmedialab/view-resources/blob/a2a4146145a49f955c5ca443ae0777da653bce64/coreview/view/partial/hydration_script/default.template.mustache#L7
        // Reason being, specifically for LAB4, the grid classes ads width:100%, which causes the adunit-wrapper to appear outside the viewport, thus not loading the ads at all.
        // Will be removed entirelly once all sites are migrated to Lab4.
        const labGridClasses = window?.Aller?.hydrated?.tempDisableAdSkinGridClassesLab4Migration
          ? ''
          : 'small-12 medium-12 large-12';
        const adskinet =
          `\
					<div class="ad-skin">\
						<div class="ad-skin__inner">\
							<div class="adunit-wrapper ad-skin__horseshoe ad-skin__horseshoe--left ${labGridClasses}">\
							<div id="ad-sticky-left-top1" class="adunit large-180x700 sticky-left" itemscope itemType="https://schema.org/WPAdBlock" data-aller-ad-placement="ad-sticky-left-top1" data-sizes=\'[[[1625,600],[[180,700],[180,500],[160,600]]],[[1365,600],[[180,700],[180,500],[160,600]]],[[1345,600],[[160,600]]], [[0,0],[]]]\' data-slot="` +
          adunitPath +
          `" data-load-on="load" data-collapse="1" data-out-of-page="false" json=\'{"targeting": { "pos": "sticky-left-top1" } }\'></div>\
							</div>\
							<div class="adunit-wrapper ad-skin__horseshoe ad-skin__horseshoe--right ${labGridClasses}">\
								<div id="ad-sticky-right-top1" class="adunit large-300x700 sticky-right" itemscope itemtype="https://schema.org/WPAdBlock" data-aller-ad-placement="ad-sticky-right-top1" data-sizes=\'[[[1625,600],[[300,600],[180,500],[180,701],[160,600]]],[[1365,600],[[180,500],[180,701],[160,600]]],[[1345,600],[[160,600]]]]\' json=\'{"targeting": { "pos":"sticky-right-top1" }}\' data-slot="` +
          adunitPath +
          '" data-load-on="load" data-collapse="1" data-out-of-page="" data-preload="1"></div>\
							</div>\
						</div>\
					</div>';
        tb.insertAdjacentHTML('afterend', adskinet);
      }
    }
  } catch (error) {
    amaLog('NO custom topbanner error', error);
  }
};

export const getNOContextTargetings = () => {
  return {
    contextCategory: window.Aller?.contextualPredictions,
    contextKey: window.Aller?.contextualKeywords,
    contextSent: window.Aller?.contextualSentiment,
  };
};

// Tags set manually on article in CMS
export const getNOKeywords = () => {
  let metakeywords = document.querySelector('meta[property="vs:keywords"]') as HTMLMetaElement;
  if (metakeywords) {
    return metakeywords
      .getAttribute('content')
      .split(',')
      .map(function (word) {
        return word.trim();
      });
  } else {
    return '';
  }
};

export const getAbTestGroup = (value: string) => {
  const numValue = parseInt(value, 10);

  if (numValue <= 2) {
    return 'alpha-1';
  } else if (numValue > 2 && numValue <= 4) {
    return 'alpha-2';
  } else if (numValue > 4 && numValue <= 6) {
    return 'alpha-3';
  } else if (numValue > 6 && numValue <= 8) {
    return 'alpha-4';
  } else if (numValue > 8 && numValue <= 10) {
    return 'alpha-5';
  } else if (numValue > 10 && numValue <= 12) {
    return 'alpha-6';
  } else if (numValue > 12 && numValue <= 14) {
    return 'alpha-7';
  } else if (numValue > 14 && numValue <= 16) {
    return 'alpha-8';
  } else if (numValue > 16 && numValue <= 18) {
    return 'alpha-9';
  } else if (numValue > 18 && numValue <= 20) {
    return 'alpha-10';
  } else if (numValue > 20 && numValue <= 25) {
    return 'beta-1';
  } else if (numValue > 25 && numValue <= 30) {
    return 'beta-2';
  } else if (numValue > 30 && numValue <= 35) {
    return 'beta-3';
  } else if (numValue > 35 && numValue <= 40) {
    return 'beta-4';
  } else if (numValue > 40 && numValue <= 45) {
    return 'gamma-1';
  } else if (numValue > 45 && numValue <= 50) {
    return 'gamma-2';
  } else if (numValue > 50 && numValue <= 55) {
    return 'gamma-3';
  } else if (numValue > 55 && numValue <= 60) {
    return 'gamma-4';
  } else if (numValue > 60 && numValue <= 65) {
    return 'delta-1';
  } else if (numValue > 65 && numValue <= 70) {
    return 'delta-2';
  } else if (numValue > 70 && numValue <= 75) {
    return 'delta-3';
  } else if (numValue > 75 && numValue <= 80) {
    return 'delta-4';
  } else if (numValue > 80 && numValue <= 85) {
    return 'omega-1';
  } else if (numValue > 85 && numValue <= 90) {
    return 'omega-2';
  } else if (numValue > 90 && numValue <= 95) {
    return 'omega-3';
  } else if (numValue > 95 && numValue <= 100) {
    return 'omega-4';
  }
  return 'N/A';
};

export const base64Encode = (string: string) => {
  return btoa(string).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Parse the cyclopsM_seg cookie and return segment data about the user.
export const getCyclopsSegments = () => {
  // Get segments from cyclopsM_seg cookie
  let encodedSegments = getCookie('cyclops_seg');

  if (encodedSegments) {
    try {
      if (window.atob) {
        return JSON.parse(b64DecodeUnicode(encodedSegments));
      }
    } catch (e) {
      return;
    }
  } else {
    return false;
  }
};

// copied from stackoverflow
const b64DecodeUnicode = (str: string) => {
  return decodeURIComponent(
    window
      .atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
};

/**
 * Read a cookie without the JSON/URI post-processing getAllCookies() applies.
 * allerM_seg2 holds base64 that can carry "=" padding, and the shared getCookie()
 * splits on "=" and would truncate the value.
 */
const getRawCookie = (name: string): string => {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  const hit = parts.find((part) => part.startsWith(prefix));
  return hit ? hit.slice(prefix.length) : '';
};

// The cookie does not change within a page view, and both setters below ask for
// the same values. Only a hit is cached, so a cold visit still picks the cookie
// up once the fetch lands.
let cachedUserData: string[] | undefined;

/**
 * Read the allerM_seg2 cookie and return the user's first-party signals as
 * { ud: [base64 names] }. A name being present means it applies to the user.
 *
 * Mirrors the am-assets implementation so both platforms send the same values
 * under the same single GAM key.
 */
export const getUserData = (): { ud?: string[] } => {
  if (cachedUserData) return { ud: cachedUserData };

  const cookieData = getRawCookie('allerM_seg2');
  if (cookieData === '') {
    amaLog('[NO] ud - "allerM_seg2" cookie not set yet');
    return {};
  }

  let data: { tags?: string[] };
  try {
    // Restore the "=" padding a cookie round-trip may have dropped.
    const padded = cookieData.padEnd(Math.ceil(cookieData.length / 4) * 4, '=');
    data = JSON.parse(decodeURIComponent(atob(padded)));
  } catch (error) {
    amaAlert('[NO] ud - failed to parse "allerM_seg2" cookie', error);
    return {};
  }

  if (!Array.isArray(data?.tags)) {
    amaWarn('[NO] ud - no tags array in "allerM_seg2"', data);
    return {};
  }

  // Prefixes we consume. pred_ is an allowlist of known families — anything
  // else (pred_fox, pred_meninger, ...) is dropped so it can't leak to GAM.
  const acceptedPrefixes = ['login_', 'content_', 'consent_', 'pred_content_', 'pred_gender_', 'pred_age_'];

  // Gender and age put the answer in the score, so 0.0 is not an absence.
  // Fold it into the name before the _0.0 filter below drops half the users.
  const scoreLabels: Record<string, Record<string, string>> = {
    gender: { '1.0': 'male', '0.0': 'female' },
    age: { '1.0': 'under_45', '0.0': 'over_45' },
  };

  const values = data.tags
    .filter((tag) => acceptedPrefixes.some((prefix) => tag.startsWith(prefix)))
    .flatMap((tag) => {
      const scored = /^pred_(gender|age)_(.+)_score_(\d+\.\d+)$/.exec(tag);
      if (!scored) return [tag];

      const [, family, device, score] = scored;
      const label = scoreLabels[family][score];
      if (!label) {
        amaWarn('[NO] ud - unexpected pred score in "allerM_seg2":', tag);
        return [];
      }
      return [`pred_${family}_${device}_${label}`];
    })
    // For the remaining tags 0.0 means absent. Gender/age were already
    // rewritten above so their 0.0 (female / over_45) survives this.
    .filter((tag) => !tag.endsWith('_0.0'))
    // Drop the trailing version, e.g. _1.0. Codes like _0301 are left alone.
    .map((tag) => tag.replace(/_\d+\.\d+$/, ''))
    // Shorten the long families — GAM caps a value at 40 encoded chars.
    .map((name) => name.replace(/^login_municipality_/, 'login_mty_').replace(/^pred_content_/, 'content_'))
    .flatMap((name) => {
      // The cookie is user-writable and btoa() throws above U+00FF.
      if (!/^[\x20-\x7E]+$/.test(name)) {
        amaWarn('[NO] ud - skipping non-ASCII "allerM_seg2" value:', name);
        return [];
      }
      return [base64Encode(name)];
    });

  if (!values.length) {
    amaLog('[NO] ud - no tag survived filtering', data.tags);
    return {};
  }

  // Dedupe: content_x and pred_content_x collapse to the same name, and a repeated
  // value in one key is wasted payload.
  cachedUserData = Array.from(new Set(values));

  // One key, many values — targeted in GAM with "is any of".
  return { ud: cachedUserData };
};

// Purpose 1 + 4 consent, mirrored from AdsManager so the setters below can be
// called from anywhere without threading consent through every call site.
let personalizedAdsConsent = false;

export const setPersonalizedAdsConsent = (hasConsent: boolean): void => {
  personalizedAdsConsent = !!hasConsent;
};

// True once user data has been queued for application (via googletag.cmd), not merely attempted.
let userDataTargetingSet = false;

// True once user data has been merged into Prebid's ortb2, not merely attempted.
let prebidUserDataSet = false;

/**
 * Send unified user data to GAM as page-level targeting: the values describe the
 * user, not the placement, so setting them once covers every slot including
 * lazily defined ones. allerM_seg2 is written by a fetch and so is missing on a
 * first visit — call this freely, it retries until found, then latches off.
 */
export const setUserDataTargeting = (): void => {
  if (userDataTargetingSet) return;

  if (!personalizedAdsConsent) {
    amaLog('[NO] ud - no personalized ads consent');
    return;
  }

  const ud = getUserData().ud;
  if (!ud?.length) return;

  if (!window.googletag?.cmd) {
    amaLog('[NO] ud - googletag not available yet, will retry');
    return;
  }

  window.googletag.cmd.push(() => {
    window.googletag.setConfig({ targeting: { ud } });
  });

  userDataTargetingSet = true;
  amaLog('[NO] ud - page-level targeting set', ud);
};

/**
 * Push unified user data into Prebid's ortb2.user.ext.data so bidders receive
 * the same signals GAM gets under the "ud" key.
 *
 * Merges rather than replacing: setConfig({ortb2}) would drop site.ext.data and
 * device along with it.
 */
export const setPrebidUserData = (): void => {
  if (prebidUserDataSet) return;

  if (!personalizedAdsConsent) {
    amaLog('[NO] ud - prebid: no personalized ads consent');
    return;
  }

  const ud = getUserData().ud;

  // allerM_seg2 is written by a fetch, so a first visit can get here before it
  // lands. Bail without latching so the next caller retries.
  if (!ud?.length) {
    amaLog('[NO] ud - prebid: no user data yet, will retry');
    return;
  }

  // Latch before queueing so a second caller does not queue a duplicate merge.
  prebidUserDataSet = true;

  // Queued, so a call made before Demand Manager has loaded still lands — and
  // once it has loaded the queue runs the callback immediately.
  pbjs.que.push(() => {
    // mergeConfig only — setConfig({ortb2}) would replace the whole ortb2 object
    // and take site.ext.data and device with it.
    if (typeof pbjs?.mergeConfig !== 'function') {
      prebidUserDataSet = false;
      amaWarn('[NO] ud - prebid: pbjs.mergeConfig not available');
      return;
    }

    // Patch only the path we own so site.ext.data, device and any bidder-level
    // overrides survive the merge.
    pbjs.mergeConfig({
      ortb2: { user: { ext: { data: { ud } } } },
    });

    amaLog('[NO] ud - prebid: ortb2.user.ext.data.ud applied', ud);
  });
};

adsFullscreen.init();
